import { defineStore } from 'pinia';
import router from '@/router';
import axios from 'axios';
import { auth } from '@/firebaseConfig';
import { signInWithEmailAndPassword, signOut, onAuthStateChanged } from 'firebase/auth';

export const useAuthStore = defineStore('auth', {
  state: () => ({
    user: null,
    isAuthenticated: false,
    loginError: null,
    isInitialized: false,
    customClaims: {},
  }),
  
  getters: {
    currentUser: (state) => state.user,
    authStatus: (state) => state.isAuthenticated,
    loginErrorMessage: (state) => state.loginError,
    isSuperAdmin: (state) => state.customClaims?.claim === 'superadmin',
  },
  
  actions: {
    setUser(user, customClaims = {}) {
      this.user = user;
      this.isAuthenticated = !!user;
      this.customClaims = user ? customClaims : {};
      if (user) {
        localStorage.setItem('user', JSON.stringify(user));
      } else {
        localStorage.removeItem('user');
      }
    },
    
    setLoginError(error) {
      console.log('Setting login error', error);
      this.loginError = error;
    },
    
    clearUser() {
      this.user = null;
      this.isAuthenticated = false;
      this.customClaims = {};
      localStorage.removeItem('user');
    },
    
    async login({ email, password }) {
      try {
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;
        const tokenResult = await user.getIdTokenResult();
        
        // Set the token in axios defaults
        axios.defaults.headers.common['Authorization'] = `Bearer ${tokenResult.token}`;
        
        this.setUser(user, tokenResult.claims);
        this.setLoginError(null);
        return { success: true };
      } catch (error) {
        console.error('Login error:', error);
        this.setLoginError('An unexpected error occurred. Please try again.');
        return { success: false, error: 'An unexpected error occurred. Please try again.' };
      }
    },
    
    async logout() {
      try {
        await signOut(auth);
        this.clearUser();
        delete axios.defaults.headers.common['Authorization'];
        router.push('/login');
      } catch (error) {
        console.error('Logout error:', error);
      }
    },
    
    initializeAuth() {
        return new Promise((resolve) => {
          const unsubscribe = onAuthStateChanged(auth, async (user) => {
            try {
              if (user) {
                const tokenResult = await user.getIdTokenResult();
                axios.defaults.headers.common['Authorization'] = `Bearer ${tokenResult.token}`;
                this.setUser(user, tokenResult.claims);
              } else {
                this.clearUser();
              }
            } catch (error) {
              console.error('Failed to load authentication claims:', error);
              if (user) this.setUser(user);
              else this.clearUser();
            } finally {
              this.isInitialized = true;
              unsubscribe();
              resolve();
            }
          });
        });
      },
    },
  });
